import React from "react";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { Input, Textarea, Field, LocalizedInput, CONTENT_LANGS } from "../../components/admin/AdminUI";
import { SingleImageUploader } from "../../components/admin/MediaUploader";
import { SubFields } from "./SubFields";

/* Bullet-point lists per language, one textarea per line, tabs kept local. */
function LocalizedLines({ label, value, onChange, testid }) {
  const [lang, setLang] = React.useState("en");
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <span className="mono text-[9px] text-[#6B675F]">{label}</span>
        <div className="inline-flex border border-[#E2E0D8] shrink-0">
          {CONTENT_LANGS.map((l) => {
            const filled = (value[l] || []).length > 0;
            return (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                data-testid={`${testid}-tab-${l}`}
                className={`mono text-[8.5px] px-3 py-1.5 flex items-center gap-1.5 transition-colors duration-300 ${
                  lang === l ? "bg-[#141414] text-[#FAFAFA]" : "bg-white text-[#141414]/55 hover:text-[#141414]"
                }`}
              >
                {l.toUpperCase()}
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    filled ? (lang === l ? "bg-[#C8B29B]" : "bg-[#8C7862]") : "bg-[#E2E0D8]"
                  }`}
                />
              </button>
            );
          })}
        </div>
      </div>
      <Textarea
        rows={3}
        value={(value[lang] || []).join("\n")}
        onChange={(e) => onChange({ ...value, [lang]: e.target.value.split("\n").filter((l) => l.trim() !== "") })}
        data-testid={`${testid}-${lang}`}
      />
    </div>
  );
}

export function FieldRenderer({ field, value, onChange, path }) {
  const testid = `field-${path}`;

  if (field.type === "text") {
    return (
      <Field label={field.label}>
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      </Field>
    );
  }

  if (field.type === "select") {
    const options = field.options || [];
    return (
      <Field label={field.label} hint={field.hint}>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-white border border-[#E2E0D8] px-4 py-3 text-[14px] text-[#141414] outline-none focus:border-[#C8B29B] transition-colors"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  if (field.type === "bool") {
    const options = [
      { value: true, label: "GÖSTER" },
      { value: false, label: "GİZLE" },
    ];
    return (
      <div className="flex items-center justify-between gap-4 py-1">
        <span className="mono text-[9px] text-[#6B675F]">{field.label}</span>
        <div className="inline-flex border border-[#E2E0D8] shrink-0" data-testid={testid}>
          {options.map((o) => {
            const on = !!value === o.value;
            return (
              <button
                key={String(o.value)}
                type="button"
                onClick={() => onChange(o.value)}
                aria-pressed={on}
                data-testid={`${testid}-${o.value ? "show" : "hide"}`}
                className={`mono text-[8.5px] px-3.5 py-2 transition-colors duration-300 ${
                  on
                    ? o.value
                      ? "bg-[#141414] text-[#FAFAFA]"
                      : "bg-[#EFECE6] text-[#141414]"
                    : "bg-white text-[#A8A29A] hover:text-[#141414]"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.type === "loc") {
    return (
      <LocalizedInput
        label={field.label}
        value={value || { en: "", fi: "" }}
        onChange={onChange}
        multiline={field.multiline}
        rows={field.multiline ? 4 : 2}
        testid={testid}
      />
    );
  }

  if (field.type === "image") {
    return <SingleImageUploader label={field.label} value={value || ""} onChange={onChange} testid={testid} />;
  }

  if (field.type === "strLines") {
    return (
      <Field label={field.label}>
        <Textarea
          rows={3}
          value={(value || []).join("\n")}
          onChange={(e) => onChange(e.target.value.split("\n").filter((l) => l.trim() !== ""))}
          data-testid={testid}
        />
      </Field>
    );
  }

  if (field.type === "locStrings") {
    const v = value || {};
    return (
      <LocalizedLines label={field.label} value={v} onChange={onChange} testid={testid} />
    );
  }

  if (field.type === "locArray") {
    const arr = value || [];
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="mono text-[9px] text-[#6B675F]">{field.label}</span>
          <button
            type="button"
            onClick={() => onChange([...arr, { en: "", fi: "" }])}
            className="mono text-[9px] border border-[#E2E0D8] bg-white px-3 py-2 hover:border-[#C8B29B] transition-colors"
            data-testid={`${testid}-add`}
          >
            <Plus size={11} className="inline mr-1" /> EKLE
          </button>
        </div>
        <div className="space-y-4">
          {arr.map((item, i) => (
            <div key={i} className="relative border-l-2 border-[#E2E0D8] pl-4">
              <LocalizedInput
                label={`PARAGRAF ${i + 1}`}
                testid={`${testid}-${i}`}
                value={item}
                onChange={(v) => onChange(arr.map((x, k) => (k === i ? v : x)))}
                multiline
                rows={4}
              />
              <button
                type="button"
                onClick={() => onChange(arr.filter((_, k) => k !== i))}
                className="mono text-[9px] text-[#8C2E1D] mt-2"
              >
                SİL
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "list") {
    const arr = value || [];
    const move = (i, d) => {
      const next = [...arr];
      const j = i + d;
      if (j < 0 || j >= next.length) return;
      [next[i], next[j]] = [next[j], next[i]];
      onChange(next);
    };
    const labelOf = (item) => {
      const raw = item[field.itemLabel];
      return (typeof raw === "object" ? raw?.en : raw) || "(isimsiz)";
    };

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <span className="mono text-[9px] text-[#6B675F]">
            {field.label} ({arr.length})
          </span>
          <button
            type="button"
            onClick={() => onChange([...arr, JSON.parse(JSON.stringify(field.newItem || {}))])}
            className="mono text-[9px] border border-[#E2E0D8] bg-white px-3.5 py-2.5 hover:border-[#C8B29B] transition-colors"
            data-testid={`${testid}-add`}
          >
            <Plus size={11} className="inline mr-1" /> EKLE
          </button>
        </div>
        <div className="space-y-3">
          {arr.map((item, i) => (
            <div key={i} className="border border-[#E2E0D8] bg-[#FAFAFA]">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E2E0D8]">
                <span className="mono text-[8.5px] text-[#A8A29A] w-6">{String(i + 1).padStart(2, "0")}</span>
                <span className="flex-1 text-[14px] text-[#141414] truncate">{labelOf(item)}</span>
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  aria-label="Yukarı taşı"
                  data-testid={`${testid}-move-up-${i}`}
                  className="p-1.5 text-[#6B675F] hover:text-[#141414]"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  aria-label="Aşağı taşı"
                  data-testid={`${testid}-move-down-${i}`}
                  className="p-1.5 text-[#6B675F] hover:text-[#141414]"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => onChange(arr.filter((_, k) => k !== i))}
                  className="p-1.5 text-[#8C2E1D]"
                  data-testid={`${testid}-remove-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="p-4 lg:p-6 space-y-5 bg-white">
                <SubFields
                  fields={field.fields}
                  item={item}
                  path={`${path}-${i}`}
                  onItemChange={(name, v) => onChange(arr.map((x, k) => (k === i ? { ...x, [name]: v } : x)))}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

